import { useEffect, useState } from 'react'
import { abonner, etatDeLaFile, type EtatDeLaFile } from '@/lib/hors-ligne/file'

/**
 * En ligne, hors ligne, N réponses en attente — RG-9.7 précisée, §111.
 *
 * **Il ne s'affiche que quand il informe.** La règle dit « en permanence » ; en
 * ligne et file vide, il n'y a pourtant rien à dire. Un indicateur permanent
 * « en ligne » est un bruit de fond dont on apprend à ne plus rien lire — ce qui
 * le rendrait invisible le jour où il compte.
 *
 * **Il rassure avant d'alerter.** « Hors connexion » seul se lit comme une
 * panne, et un candidat qui croit que ses réponses se perdent arrête de
 * répondre. La phrase dit donc d'abord ce qui est vrai : elles sont gardées.
 */
export function EtatDuReseau() {
  const [etat, setEtat] = useState<EtatDeLaFile>(etatDeLaFile)

  useEffect(() => abonner(setEtat), [])

  if (etat.etat === 'en-ligne' && etat.enAttente === 0) return null

  const message = phrase(etat)

  return (
    <div
      // `role="status"` et non `alert` : c'est un état, pas une interruption —
      // un lecteur d'écran ne doit pas couper la lecture d'une question pour
      // l'annoncer.
      role="status"
      className="flex items-center justify-center gap-2 px-4 py-2 text-center text-[13px]"
      style={{
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--bordure)',
        color: 'var(--texte-2)',
      }}
    >
      <Pastille etat={etat.etat} />
      {message}
    </div>
  )
}

function phrase(etat: EtatDeLaFile): string {
  const n = etat.enAttente
  const reponses = `${String(n)} réponse${n > 1 ? 's' : ''}`

  if (etat.etat === 'synchronisation') {
    return n === 0 ? 'Envoi en cours…' : `Envoi de ${reponses}…`
  }

  if (etat.etat === 'hors-ligne') {
    return n === 0
      ? 'Hors connexion. Tu peux continuer : tes réponses seront envoyées au retour du réseau.'
      : `Hors connexion — ${reponses} gardée${n > 1 ? 's' : ''}, elles partiront au retour du réseau.`
  }

  // En ligne avec de l'attente : le vidage n'a pas encore repris la main.
  return `${reponses} en attente d’envoi.`
}

/**
 * Un point, et non une icône.
 *
 * L'état du réseau n'a pas de geste associé : une icône appellerait un clic qui
 * ne fait rien. Le point pulse pendant l'envoi — c'est le seul des trois états
 * qui bouge tout seul.
 */
function Pastille({ etat }: { etat: EtatDeLaFile['etat'] }) {
  return (
    <span
      aria-hidden="true"
      className={etat === 'synchronisation' ? 'animate-pulse' : undefined}
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        flexShrink: 0,
        background: etat === 'hors-ligne' ? 'var(--texte-2)' : 'var(--accent-fort)',
      }}
    />
  )
}
